import React, { useEffect, useState } from 'react';
import { withFormik, Field, Form, getIn } from 'formik';
import { Button, Row, Col, Card, Accordion, Modal, Form as BSForm } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import * as Yup from 'yup';

import {
  CheckBoxBtn,
  FormikDatePicker,
  FormikInput,
  FormikMaterialUiTimePicker,
  FormikNumber,
  FormikRangePicker,
  FormikSelect,
  NestedAttributes,
  UploadFile,
  UploadImage,
  InputSelect,
  RegionCommune
} from '../../components';
import { debounceIndexCountriesRequest } from '../../requests/countries';

import { rutFormat, validRutInput } from '../../services/utils';
import '../../services/yupCustomMethods';
import { modality, activity, statuses, identificationTypes, franchiseTypeOptions } from './FormOptions';
import './style.scss';

const InstructorInput = ({ errors, touched, index, instructorAttribute, setFieldValue, setFieldTouched }) => {
  const handleNationalIdentificationFormat = (e, isRut) => {
    if (isRut && validRutInput(e)) {
      const formattedValue = rutFormat(e.target.value);
      setFieldValue(e.target.name, formattedValue);
    } else {
      setFieldValue(e.target.name, e.target.value);
    }
  };

  return (
    <>
      <Col md={3}>
        <Field name={`course[instructorsAttributes][${index}][name]`}>
          {({ field }) => (
            <FormikInput
              {...field}
              abbr
              label="Nombre y Apellido"
              error={getIn(errors, field.name)}
              touched={getIn(touched, field.name)}
            />
          )}
        </Field>
      </Col>
      <Col md={3}>
        <Field name={`course[instructorsAttributes][${index}][identificationType]`}>
          {({ field }) => (
            <FormikSelect
              {...field}
              defaultValue={instructorAttribute.identificationType}
              label="Tipo de Identificación"
              options={identificationTypes}
              placeholder="Seleccionar"
              onChange={data => {
                setFieldValue(`course[instructorsAttributes][${index}][nationalIdentification]`, '');
                setFieldValue(field.name, data ? data.value : 'rut');
              }}
              setFieldTouched={() => setFieldTouched(field.name)}
              error={getIn(errors, field.name)}
              touched={getIn(touched, field.name)}
            />
          )}
        </Field>
      </Col>
      <Col md={3}>
        <Field name={`course[instructorsAttributes][${index}][nationalIdentification]`}>
          {({ field }) => (
            <FormikInput
              {...field}
              label={instructorAttribute.identificationType === 'rut' ? 'RUT' : 'N° de Identificación'}
              onChange={e => handleNationalIdentificationFormat(e, instructorAttribute.identificationType === 'rut')}
              error={getIn(errors, field.name)}
              touched={getIn(touched, field.name)}
            />
          )}
        </Field>
      </Col>
      <Col md={3}>
        <Field name={`course[instructorsAttributes][${index}][position]`}>
          {({ field }) => (
            <FormikInput
              {...field}
              label="Cargo"
              error={getIn(errors, field.name)}
              touched={getIn(touched, field.name)}
            />
          )}
        </Field>
      </Col>
    </>
  );
};

const MODALITIES_WITH_ADDRESS = ['on_site', 'mixed'];

const CourseForm = ({
  errors,
  touched,
  values,
  setFieldValue,
  setFieldTouched,
  isSubmitting,
  submitCount,
  isValid
}) => {
  const {
    startDate,
    endDate,
    courseModality,
    activityType,
    monday,
    tuesday,
    wednesday,
    thursday,
    friday,
    saturday,
    sunday,
    bipartiteCommittee,
    senceRegister,
    instructorsAttributes,
    courseCertificate,
    courseImage,
    country,
    franchiseType
  } = values.course;

  const vBipartiteCommittee = !bipartiteCommittee;
  const isChile = country?.label === 'Chile';
  const dispatch = useDispatch();
  const [activeAccordion, setActiveAccordion] = useState('general');
  const [isAddressRequired, setIsAddressRequired] = useState(false);

  const generalFields = [
    'name',
    'theme',
    'courseAgency',
    'activityType',
    'courseModality',
    'maxAvailability',
    'duration',
    'limitDate',
    'franchiseType',
    'status',
    'description',
    'courseImage',
    'courseCertificate'
  ];

  const dateFields = [
    'startDate',
    'endDate',
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday',
    'startTime',
    'endTime',
    'countryId',
    'regionId',
    'communeId',
    'address',
    'addressNumber',
    'addressApt',
    'connectionLink'
  ];

  const costFields = ['costPerParticipant', 'totalValue', 'senceCode', 'mandateIndicator', 'bipartiteCommittee'];

  const hasError = fields => fields.some(field => getIn(errors, `course.${field}`));

  const hasInstructorErrors = () => {
    const instructorErrors = getIn(errors, 'course.instructorsAttributes');
    return instructorErrors && (Array.isArray(instructorErrors) ? instructorErrors.some(e => e) : true);
  };

  const generalHasErrors = hasError(generalFields);
  const dateHasErrors = hasError(dateFields) || getIn(errors, 'rangeDate');
  const costHasErrors = hasError(costFields);
  const instructorHasErrors = hasInstructorErrors();

  useEffect(() => {
    if (submitCount > 0 && !isValid) {
      if (generalHasErrors) setActiveAccordion('general');
      else if (dateHasErrors) setActiveAccordion('fechas');
      else if (instructorHasErrors) setActiveAccordion('instructores');
      else if (costHasErrors) setActiveAccordion('costo');
    }
  }, [submitCount, isValid, generalHasErrors, dateHasErrors, instructorHasErrors, costHasErrors]);

  const addInstructor = () => {
    const mapResults = instructorsAttributes.map((body, index) => {
      if (body._destroy) {
        return undefined;
      }

      return (
        <InstructorInput
          key={`instructor-${index.toString()}`}
          errors={errors}
          touched={touched}
          index={index}
          instructorAttribute={body}
          setFieldValue={setFieldValue}
          setFieldTouched={setFieldTouched}
        />
      );
    });

    mapResults.push(
      <>
        <Col md={3} className="sample-row">
          <FormikInput label="Nombre y Apellido" disabled />
        </Col>
        <Col md={3} className="sample-row">
          <FormikInput label="Tipo de Identificación" disabled />
        </Col>
        <Col md={3} className="sample-row">
          <FormikInput label="N° de Identificación" disabled />
        </Col>
        <Col md={3} className="sample-row">
          <FormikInput label="Cargo" disabled />
        </Col>
      </>
    );

    return (
      <>
        <NestedAttributes
          removeFirstItem
          mapInputs={mapResults}
          arrayValues={instructorsAttributes}
          setFieldValue={setFieldValue}
          valuePath="course[instructorsAttributes]"
          newAttributes={{ identificationType: '', nationalIdentification: '', name: '', position: '' }}
        />
      </>
    );
  };

  const resultFetchData = response => {
    const result = response.data.data;
    return result.map(element => ({
      ...element,
      label: element.label,
      value: element.value
    }));
  };

  const fetchCountries = (inputValue, callback) => {
    debounceIndexCountriesRequest({
      dispatch,
      params: {
        name: inputValue,
        sort_column: 'position',
        display_length: 250
      },
      successCallback: data => {
        callback(resultFetchData(data));
      }
    });
  };

  const handleRegionCommuneDisabled = () => {
    if (isChile) return;
    setFieldValue('course[regionId]', '');
    setFieldValue('course[communeId]', '');
  };
  useEffect(handleRegionCommuneDisabled, [isChile]);

  const isCourseModality = values.course?.courseModality;

  useEffect(() => {
    if (MODALITIES_WITH_ADDRESS.includes(isCourseModality)) {
      setIsAddressRequired(true);
    } else {
      setIsAddressRequired(false);
      setFieldValue('course[address]', '');
      setFieldValue('course[addressNumber]', '');
      setFieldValue('course[addressApt]', '');
      setFieldValue('course[countryId]', '');
      setFieldValue('course[regionId]', '');
      setFieldValue('course[communeId]', '');
    }
  }, [isCourseModality, setFieldValue]);

  return (
    <Form className="container-fluid">
      <Modal.Body className="pb-0">
        <div className="yellow-wrapper">
          <Accordion activeKey={activeAccordion} onSelect={key => setActiveAccordion(key)}>
            <Col className={`${activeAccordion === 'general' ? 'top-header-green' : 'top-header-light-gray'} br-5`}>
              <Accordion.Toggle
                as={Col}
                eventKey="general"
                className="card-header-title"
                onClick={() => setActiveAccordion(activeAccordion === 'general' ? null : 'general')}
                style={{ cursor: 'pointer' }}
              >
                <span>Datos Generales</span>
              </Accordion.Toggle>
            </Col>

            <Accordion.Collapse eventKey="general">
              <Card>
                <Card.Body>
                  <Row>
                    <Col md={3} className="pr-5">
                      <Field name="course[name]">
                        {({ field }) => (
                          <FormikInput
                            {...field}
                            abbr
                            label="Nombre"
                            error={getIn(errors, field.name)}
                            touched={getIn(touched, field.name)}
                          />
                        )}
                      </Field>
                    </Col>

                    <Col md={3} className="pr-5">
                      <Field name="course[theme]">
                        {({ field }) => (
                          <FormikInput
                            {...field}
                            abbr
                            label="Tema"
                            tooltipText="Categoría a la que pertenece el curso"
                            error={getIn(errors, field.name)}
                            touched={getIn(touched, field.name)}
                          />
                        )}
                      </Field>
                    </Col>

                    <Col md={3} className="pr-5">
                      <Field name="course[courseAgency]">
                        {({ field }) => (
                          <FormikInput
                            {...field}
                            abbr
                            label="Empresa/Organismo que Imparte Capacitación"
                            tooltipText="Entidad o empresa que imparte el curso"
                            error={getIn(errors, field.name)}
                            touched={getIn(touched, field.name)}
                          />
                        )}
                      </Field>
                    </Col>

                    <Col md={3} className="pr-5">
                      <Field name="course[activityType]">
                        {({ field }) => (
                          <FormikSelect
                            {...field}
                            abbr
                            label="Tipo de Actividad"
                            placeholder="Seleccionar actividad"
                            options={activity}
                            defaultValue={activityType}
                            onChange={data => setFieldValue(field.name, data ? data.value : '')}
                            setFieldTouched={() => setFieldTouched(field.name)}
                            error={getIn(errors, field.name)}
                            touched={getIn(touched, field.name)}
                          />
                        )}
                      </Field>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={3} className="pr-5">
                      <Field name="course[courseModality]">
                        {({ field }) => (
                          <FormikSelect
                            {...field}
                            abbr
                            label="Modalidad"
                            tooltipText="Tipo de modalidad que será impartido el curso"
                            placeholder="Seleccionar modalidad"
                            options={modality}
                            defaultValue={courseModality}
                            onChange={data => setFieldValue(field.name, data ? data.value : '')}
                            setFieldTouched={() => setFieldTouched(field.name)}
                            error={getIn(errors, field.name)}
                            touched={getIn(touched, field.name)}
                          />
                        )}
                      </Field>
                    </Col>
                    <Col md={3} className="pr-5">
                      <Field name="course[parsedMaxAvailability]">
                        {({ field }) => (
                          <FormikNumber
                            {...field}
                            abbr
                            label="Cupos Disponibles"
                            fieldName="course[maxAvailability]"
                            setFieldValue={setFieldValue}
                            value={getIn(values, 'course[maxAvailability]')}
                            errors={errors}
                            touched={touched}
                          />
                        )}
                      </Field>
                    </Col>
                    <Col md={3} className="pr-5">
                      <Field name="course[duration]">
                        {({ field }) => (
                          <FormikInput
                            {...field}
                            abbr
                            label="Duración"
                            error={getIn(errors, field.name)}
                            touched={getIn(touched, field.name)}
                          />
                        )}
                      </Field>
                    </Col>
                    <Col md={3} className="pr-5">
                      <Field name="course[limitDate]">
                        {({ field }) => (
                          <FormikDatePicker
                            {...field}
                            abbr
                            isOutsideRange={() => false}
                            label="Fecha Límite de Inscripción"
                            placeholder="dd/mm/aaaa"
                            error={getIn(errors, field.name)}
                            touched={getIn(touched, field.name)}
                          />
                        )}
                      </Field>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={3} className="pr-5">
                      <Field name="course[franchiseType]">
                        {({ field }) => (
                          <FormikSelect
                            {...field}
                            abbr
                            label="Tipo de Franquicia"
                            placeholder="Seleccionar franquicia"
                            options={franchiseTypeOptions}
                            defaultValue={franchiseType}
                            onChange={data => setFieldValue(field.name, data ? data.value : '')}
                            setFieldTouched={() => setFieldTouched(field.name)}
                            error={getIn(errors, field.name)}
                            touched={getIn(touched, field.name)}
                          />
                        )}
                      </Field>
                    </Col>
                    <Col md={3} className="pr-5">
                      <Field name="course[status]">
                        {({ field }) => (
                          <FormikSelect
                            {...field}
                            abbr
                            label="Estado"
                            tooltipText="Indica el estado actual del curso"
                            placeholder="Seleccionar estado"
                            options={statuses}
                            defaultValue={values.course.status}
                            onChange={data => setFieldValue(field.name, data ? data.value : '')}
                            setFieldTouched={() => setFieldTouched(field.name)}
                            error={getIn(errors, field.name)}
                            touched={getIn(touched, field.name)}
                          />
                        )}
                      </Field>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={12} className="pr-5">
                      <Field name="course[description]">
                        {({ field }) => (
                          <FormikInput
                            {...field}
                            as="textarea"
                            label="Descripción"
                            minInputHeight="160"
                            error={getIn(errors, field.name)}
                            touched={getIn(touched, field.name)}
                          />
                        )}
                      </Field>
                    </Col>
                  </Row>

                  <Row className="mt-3">
                    <Col md={4} className="pr-5">
                      <Field name="course[courseImage]">
                        {({ field }) => (
                          <UploadImage
                            {...field}
                            name="Agregar imagen"
                            label="Banner del Curso"
                            imageUrl={getIn(courseImage, 'fileUrl')}
                            onChange={img => setFieldValue(field.name, img)}
                            helpText="Formato sugerido 320×200px máx 5mb."
                            fileAccept=".png, .jpg, .jpeg"
                            error={getIn(errors, field.name)}
                            touched={getIn(touched, field.name)}
                          />
                        )}
                      </Field>
                    </Col>

                    <Col md={4} className="pr-5">
                      <Field name="course[courseCertificate]">
                        {({ field }) => (
                          <UploadFile
                            {...field}
                            label="Certificado / Diploma"
                            tooltipText="Formato sugerido 320×200px - 5mb"
                            formats={['.docx', '.png', '.jpeg', '.pdf', '.jpg', '.doc', '.rtf']}
                            name={courseCertificate?.filename || 'Adjuntar Archivo'}
                            onChange={file => setFieldValue(field.name, file)}
                            error={getIn(errors, field.name)}
                            touched={getIn(touched, field.name)}
                          />
                        )}
                      </Field>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Accordion.Collapse>
          </Accordion>

          <Accordion className="my-2" activeKey={activeAccordion} onSelect={key => setActiveAccordion(key)}>
            <Col className={`${activeAccordion === 'fechas' ? 'top-header-green' : 'top-header-light-gray'} br-5`}>
              <Accordion.Toggle
                as={Col}
                eventKey="fechas"
                className="card-header-title"
                onClick={() => setActiveAccordion(activeAccordion === 'fechas' ? null : 'fechas')}
                style={{ cursor: 'pointer' }}
              >
                <span>Fechas y Dirección de la Capacitación</span>
              </Accordion.Toggle>
            </Col>

            <Accordion.Collapse eventKey="fechas">
              <Card>
                <Card.Body>
                  <Row>
                    <Col md={3}>
                      <Field name="rangeDate">
                        {({ field }) => (
                          <FormikRangePicker
                            {...field}
                            abbr
                            startDateName="course[startDate]"
                            endDateName="course[endDate]"
                            startDate={startDate}
                            endDate={endDate}
                            showClearDates
                            error={getIn(errors, field.name)}
                            touched={getIn(touched, field.name)}
                          />
                        )}
                      </Field>
                    </Col>
                    <Col md={5}>
                      <BSForm.Group className="button-group max-width-group">
                        <BSForm.Label>Días de Clases</BSForm.Label>
                        <Row>
                          <Field name="course[monday]">
                            {({ field }) => <CheckBoxBtn {...field} id="monday" label="Lu" checked={monday} />}
                          </Field>
                          <Field name="course[tuesday]">
                            {({ field }) => <CheckBoxBtn {...field} id="tuesday" label="Ma" checked={tuesday} />}
                          </Field>
                          <Field name="course[wednesday]">
                            {({ field }) => <CheckBoxBtn {...field} id="wednesday" label="Mi" checked={wednesday} />}
                          </Field>
                          <Field name="course[thursday]">
                            {({ field }) => <CheckBoxBtn {...field} id="thursday" label="Ju" checked={thursday} />}
                          </Field>
                          <Field name="course[friday]">
                            {({ field }) => <CheckBoxBtn {...field} id="friday" label="Vi" checked={friday} />}
                          </Field>
                          <Field name="course[saturday]">
                            {({ field }) => <CheckBoxBtn {...field} id="saturday" label="Sa" checked={saturday} />}
                          </Field>
                          <Field name="course[sunday]">
                            {({ field }) => <CheckBoxBtn {...field} id="sunday" label="Do" checked={sunday} />}
                          </Field>
                        </Row>
                      </BSForm.Group>
                    </Col>
                    <Col md={2}>
                      <Field name="course[startTime]">
                        {({ field }) => (
                          <FormikMaterialUiTimePicker
                            {...field}
                            timeSelector
                            label="Hora de inicio"
                            classNameTime="timepicker-square"
                            onChange={time => setFieldValue(field.name, time)}
                            error={getIn(errors, field.name)}
                            touched={getIn(touched, field.name)}
                          />
                        )}
                      </Field>
                    </Col>
                    <Col md={2}>
                      <Field name="course[endTime]">
                        {({ field }) => (
                          <FormikMaterialUiTimePicker
                            {...field}
                            timeSelector
                            label="Hora de Termino"
                            classNameTime="timepicker-square"
                            onChange={time => setFieldValue(field.name, time)}
                            error={getIn(errors, field.name)}
                            touched={getIn(touched, field.name)}
                          />
                        )}
                      </Field>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={12}>
                      <Row className="region-commune-row">
                        <Col md={4}>
                          <Field name="course[countryId]">
                            {({ field }) => (
                              <InputSelect
                                {...field}
                                abbr={isAddressRequired}
                                label="País"
                                placeholder="Seleccionar País"
                                defaultValue={values.course.country}
                                request={fetchCountries}
                                onChange={data => {
                                  setFieldValue(field.name, data ? data.value : '');
                                  setFieldValue('course[country]', data || {});
                                }}
                                error={getIn(errors, field.name)}
                                touched={getIn(touched, field.name)}
                              />
                            )}
                          </Field>
                        </Col>
                        <Col md={8}>
                          <Row>
                            <RegionCommune
                              regionAbbr={isChile && isAddressRequired}
                              communeAbbr={isChile && isAddressRequired}
                              modelKey="course"
                              allDisabled={!isChile}
                              md="6"
                            />
                          </Row>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={8}>
                      <Field name="course[address]">
                        {({ field }) => (
                          <FormikInput
                            {...field}
                            abbr={isAddressRequired}
                            disabled={!isAddressRequired}
                            label="Dirección de realización del curso"
                            error={getIn(errors, field.name)}
                            touched={getIn(touched, field.name)}
                          />
                        )}
                      </Field>
                    </Col>
                    <Col md={2}>
                      <Field name="course[addressNumber]">
                        {({ field }) => (
                          <FormikInput
                            {...field}
                            abbr={isAddressRequired}
                            disabled={!isAddressRequired}
                            label="Número"
                            inputType="number"
                            error={getIn(errors, field.name)}
                            touched={getIn(touched, field.name)}
                          />
                        )}
                      </Field>
                    </Col>
                    <Col md={2}>
                      <Field name="course[addressApt]">
                        {({ field }) => (
                          <FormikInput
                            {...field}
                            abbr={false}
                            label="Departamento"
                            disabled={!isAddressRequired}
                            error={getIn(errors, field.name)}
                            touched={getIn(touched, field.name)}
                          />
                        )}
                      </Field>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={4} className="">
                      <Field name="course[connectionLink]">
                        {({ field }) => (
                          <FormikInput
                            {...field}
                            abbr={isCourseModality === 'remote'}
                            label="Link de Conexión"
                            error={getIn(errors, field.name)}
                            touched={getIn(touched, field.name)}
                          />
                        )}
                      </Field>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Accordion.Collapse>
          </Accordion>

          <Accordion className="my-2" activeKey={activeAccordion} onSelect={key => setActiveAccordion(key)}>
            <Col
              className={`${activeAccordion === 'instructores' ? 'top-header-green' : 'top-header-light-gray'} br-5`}
            >
              <Accordion.Toggle
                as={Col}
                eventKey="instructores"
                className="card-header-title"
                onClick={() => setActiveAccordion(activeAccordion === 'instructores' ? null : 'instructores')}
                style={{ cursor: 'pointer' }}
              >
                <span>Instructor(es) / Relator(es)</span>
              </Accordion.Toggle>
            </Col>

            <Accordion.Collapse eventKey="instructores">
              <Card>
                <Card.Body>
                  <div className="bg-color-course p-course b-radius">{addInstructor()}</div>
                </Card.Body>
              </Card>
            </Accordion.Collapse>
          </Accordion>

          <Accordion className="my-2" activeKey={activeAccordion} onSelect={key => setActiveAccordion(key)}>
            <Col className={`${activeAccordion === 'costo' ? 'top-header-green' : 'top-header-light-gray'} br-5`}>
              <Accordion.Toggle
                as={Col}
                eventKey="costo"
                className="card-header-title"
                onClick={() => setActiveAccordion(activeAccordion === 'costo' ? null : 'costo')}
                style={{ cursor: 'pointer' }}
              >
                <span>Costo / Sence</span>
              </Accordion.Toggle>
            </Col>

            <Accordion.Collapse eventKey="costo">
              <Card>
                <Card.Body>
                  <Row>
                    <Col md={3} className="pr-5">
                      <Field name="course[parsedCostPerParticipant]">
                        {({ field }) => (
                          <FormikNumber
                            {...field}
                            abbr
                            label="Costo por Participante"
                            fieldName="course[costPerParticipant]"
                            setFieldValue={setFieldValue}
                            value={getIn(values, 'course[costPerParticipant]')}
                            errors={errors}
                            touched={touched}
                          />
                        )}
                      </Field>
                    </Col>
                    <Col md={3} className="pr-5">
                      <Field name="course[parsedTotalValue]">
                        {({ field }) => (
                          <FormikNumber
                            {...field}
                            abbr
                            label="Costo Total"
                            fieldName="course[totalValue]"
                            setFieldValue={setFieldValue}
                            value={getIn(values, 'course[totalValue]')}
                            errors={errors}
                            touched={touched}
                          />
                        )}
                      </Field>
                    </Col>
                    <Col md={3} className="pr-5">
                      <Field name="course[senceCode]">
                        {({ field }) => (
                          <FormikInput
                            {...field}
                            abbr={senceRegister}
                            label="Código Sence"
                            error={getIn(errors, field.name)}
                            touched={getIn(touched, field.name)}
                          />
                        )}
                      </Field>
                    </Col>
                    <Col md={3} className="pr-5">
                      <Field name="course[parsedMandateIndicator]">
                        {({ field }) => (
                          <FormikNumber
                            {...field}
                            label="Indicador de Mandato"
                            fieldName="course[mandateIndicator]"
                            setFieldValue={setFieldValue}
                            value={getIn(values, 'course[mandateIndicator]')}
                            errors={errors}
                            touched={touched}
                          />
                        )}
                      </Field>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={3} className="pr-5">
                      <BSForm.Group className="button-group max-width-group">
                        <BSForm.Label>Comité Bipartito</BSForm.Label>
                        <Row>
                          <Field name="course[bipartiteCommittee]">
                            {({ field }) => (
                              <CheckBoxBtn {...field} id="bipartiteCommittee" label="Si" checked={bipartiteCommittee} />
                            )}
                          </Field>
                          <Field name="course[vBipartiteCommittee]">
                            {({ field }) => (
                              <CheckBoxBtn
                                {...field}
                                id="bipartiteCommittee"
                                label="No"
                                checked={vBipartiteCommittee}
                              />
                            )}
                          </Field>
                        </Row>
                      </BSForm.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Accordion.Collapse>
          </Accordion>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button type="submit" disabled={isSubmitting} variant="primary">
          Guardar
        </Button>
      </Modal.Footer>
    </Form>
  );
};

const setInitialValues = props => {
  const { course } = props;

  return {
    course,
    rangeDate: [course.startDate, course.endDate]
  };
};

const SUPPORTED_FORMATS = [
  'image/jpg',
  'image/jpeg',
  'image/png',
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/rtf',
  'text/rtf'
];

const validationSchema = Yup.object().shape({
  course: Yup.object().shape(
    {
      activityType: Yup.string().required('Debes seleccionar una actividad'),
      address: Yup.string().when('courseModality', {
        is: val => val === 'on_site' || val === 'mixed',
        then: Yup.string().required('Debes ingresar una dirección'),
        otherwise: Yup.string().nullable()
      }),
      addressNumber: Yup.number().when('courseModality', {
        is: val => val === 'on_site' || val === 'mixed',
        then: Yup.number()
          .required('Debes ingresar un número')
          .typeError('Debes ingresar un número')
          .min(0, 'Debe ser mayor o igual a 0'),
        otherwise: Yup.number().nullable()
      }),
      communeId: Yup.string().when(['country', 'courseModality'], {
        is: (country, courseModalityValue) =>
          country?.label === 'Chile' && (courseModalityValue === 'on_site' || courseModalityValue === 'mixed'),
        then: Yup.string().required('Debes seleccionar una comuna'),
        otherwise: Yup.string().nullable()
      }),
      connectionLink: Yup.string().when('courseModality', {
        is: 'remote',
        then: Yup.string().required('Debes ingresar un link de conexión'),
        otherwise: Yup.string().nullable()
      }),
      costPerParticipant: Yup.number()
        .required('Debes ingresar un costo por participante')
        .typeError('Debes ingresar un número')
        .min(0, 'El valor no puede ser negativo'),
      countryId: Yup.string().when('courseModality', {
        is: val => val === 'on_site' || val === 'mixed',
        then: Yup.string().required('Debes seleccionar un país'),
        otherwise: Yup.string().nullable()
      }),
      courseAgency: Yup.string().required('Debes ingresar el organismo que imparte el curso'),
      courseCertificate: Yup.mixed()
        .nullable()
        .notRequired()
        .test(
          'FILE_SIZE',
          'El archivo cargado excede el tamaño maximo permitido (5mb).',
          value => !value?.size || (value && value?.size <= 5242880)
        )
        .test(
          'FILE_FORMAT',
          'El archivo cargado tiene un formato no compatible.',
          value => !value?.size || (value && SUPPORTED_FORMATS.includes(value?.type))
        ),
      courseImage: Yup.mixed()
        .nullable()
        .notRequired()
        .test(
          'FILE_SIZE',
          'La imagen cargada excede el tamaño maximo permitido (5mb).',
          value => !value?.size || (value && value?.size <= 5242880)
        )
        .test(
          'FILE_FORMAT',
          'El archivo cargado tiene un formato no compatible.',
          value => !value?.type || (value && ['image/jpg', 'image/png', 'image/jpeg'].includes(value?.type))
        ),
      courseModality: Yup.string().required('Debes seleccionar una modalidad de curso'),
      duration: Yup.string().required('Debes ingresar la duración del curso'),
      endDate: Yup.date()
        .required('Debes seleccionar una fecha de término')
        .formatdate()
        .when(
          'limitDate',
          (limitDate, schema) =>
            limitDate && schema.min(limitDate, 'Debe ser mayor o igual a la fecha limite de inscripción')
        ),
      franchiseType: Yup.string().required('Debes seleccionar un tipo de franquicia'),
      instructorsAttributes: Yup.array().of(
        Yup.object().shape(
          {
            name: Yup.string().required('Debes ingresar el nombre del relator'),
            position: Yup.string(),
            nationalIdentification: Yup.string().when('identificationType', (identificationType, schema) => {
              if (identificationType) {
                return identificationType === 'rut'
                  ? schema.required('Debes ingresar el RUT').rut('Debes ingresar un RUT válido')
                  : schema
                      .required('Debes ingresar el número de identificación')
                      .alphanumeric('Deben ser caracteres alfanuméricos');
              }
              return schema.nullable();
            }),
            identificationType: Yup.string().when('nationalIdentification', (nationalId, schema) =>
              nationalId ? schema.required('Debes ingresar el tipo de identificación') : schema.nullable()
            )
          },
          [['identificationType', 'nationalIdentification']]
        )
      ),
      limitDate: Yup.date()
        .required('Debes seleccionar una fecha limite de inscripción')
        .formatdate()
        .when(
          'endDate',
          (endDate, schema) => endDate && schema.max(endDate, 'Debe ser menor o igual a la fecha de término')
        ),
      maxAvailability: Yup.number()
        .required('Debes ingresar un máximo de cupo')
        .typeError('Debes ingresar un número')
        .positive('Debe ser mayor a 0'),
      name: Yup.string().required('Debes ingresar un nombre del curso'),
      regionId: Yup.string().when(['country', 'courseModality'], {
        is: (country, courseModalityValue) =>
          country?.label === 'Chile' && (courseModalityValue === 'on_site' || courseModalityValue === 'mixed'),
        then: Yup.string().required('Debes seleccionar una región'),
        otherwise: Yup.string().nullable()
      }),
      startDate: Yup.date().formatdate(),
      status: Yup.string().required('Debes seleccionar un estado'),
      senceCode: Yup.string().when('senceRegister', {
        is: true,
        then: Yup.string().required('Debes ingresar un código sence')
      }),
      theme: Yup.string().required('Debes ingresar un tema'),
      totalValue: Yup.number()
        .required('Debes ingresar un valor total')
        .typeError('Debes ingresar solo números')
        .min(0, 'El valor no puede ser negativo')
    },
    [['endDate', 'limitDate']]
  ),
  rangeDate: Yup.array().rangedate(true)
});

const handleSubmit = (values, { props, setSubmitting }) => {
  const { formRequest } = props;
  formRequest(values, setSubmitting);
};

export default withFormik({
  mapPropsToValues: setInitialValues,
  validationSchema,
  handleSubmit,
  enableReinitialize: true,
  validateOnMount: props => props.action !== 'new'
})(CourseForm);
